import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Calendar, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerBulkView() {
  const { bulkId } = useParams();
  const [bulkData, setBulkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [organisms, setOrganisms] = useState([]);
  const [zooplanktonOrganisms, setZooplanktonOrganisms] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [thresholdMap, setThresholdMap] = useState({});
  const [groupThresholdMap, setGroupThresholdMap] = useState({});

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/reports/bulk/${bulkId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Microsafe_rapport_${bulkId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF-rapport lastet ned');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Kunne ikke laste ned PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchBulkData = useCallback(async () => {
    try {
      // Fetch real data from API + thresholds
      const [response, algaeStructRes] = await Promise.all([
        axios.get(`${API}/bulk-registrations/${bulkId}`, { withCredentials: true }),
        axios.get(`${API}/organisms/algae-structure`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      
      const data = response.data;
      
      // Build threshold maps
      const tMap = {};
      const gMap = {};
      Object.entries(algaeStructRes.data || {}).forEach(([group, groupData]) => {
        const speciesList = Array.isArray(groupData) ? groupData : (groupData?.species || []);
        speciesList.forEach(sp => {
          tMap[`${group}_${sp.species}`] = {
            threshold_yellow: sp.threshold_yellow,
            threshold_orange: sp.threshold_orange,
            threshold_red: sp.threshold_red
          };
        });
        const gt = groupData?.group_thresholds;
        if (gt) {
          gMap[group] = {
            threshold_yellow: gt.threshold_yellow,
            threshold_orange: gt.threshold_orange,
            threshold_red: gt.threshold_red
          };
        }
      });
      setThresholdMap(tMap);
      setGroupThresholdMap(gMap);
      
      // Extract locations from algae_data
      const extractedLocations = [];
      if (data.algae_data) {
        const firstKey = Object.keys(data.algae_data)[0];
        if (firstKey && data.algae_data[firstKey]) {
          Object.keys(data.algae_data[firstKey]).forEach(loc => {
            // Strip "Lokalitet " prefix if present, then extract short name
            let cleanLoc = loc;
            if (cleanLoc.startsWith('Lokalitet ')) {
              cleanLoc = cleanLoc.substring('Lokalitet '.length);
            }
            const parts = cleanLoc.split(' - ');
            const shortName = parts.length > 1 ? parts[1] : cleanLoc;
            extractedLocations.push({ full: loc, short: shortName });
          });
        }
      }
      setLocations(extractedLocations);
      
      // Extract organisms from algae_data
      const extractedOrganisms = [];
      if (data.algae_data) {
        Object.keys(data.algae_data).forEach(key => {
          const [group, species] = key.split('_');
          if (group && species) {
            extractedOrganisms.push({
              key: key,
              group: group,
              species: species,
              isSum: species.includes('SUM')
            });
          }
        });
      }
      setOrganisms(extractedOrganisms);
      
      // Extract zooplankton organisms with standard order
      const extractedZooplankton = [];
      const standardZooOrder = [
        'Håvtrekk_Mikromaneter', 'Håvtrekk_Kolonimaneter', 'Håvtrekk_Små maneter',
        'Håvtrekk_Radiolaria', 'Håvtrekk_Pigghudlarver', 'Håvtrekk_Foraminifera',
        'Håvtrekk_Hydroider', 'Håvtrekk_Larver skjell', 'Håvtrekk_Ribbemanet',
        'Visuelt_Manet'
      ];
      if (data.zooplankton_data) {
        Object.keys(data.zooplankton_data).forEach(key => {
          const parts = key.split('_');
          const sampleType = parts[0];
          const organism = parts.slice(1).join('_');
          if (sampleType && organism) {
            extractedZooplankton.push({ key, sampleType, organism });
          }
        });
        extractedZooplankton.sort((a, b) => {
          const idxA = standardZooOrder.indexOf(a.key);
          const idxB = standardZooOrder.indexOf(b.key);
          if (idxA !== -1 && idxB !== -1) return idxA - idxB;
          if (idxA !== -1) return -1;
          if (idxB !== -1) return 1;
          return a.sampleType.localeCompare(b.sampleType) || a.organism.localeCompare(b.organism);
        });
      }
      setZooplanktonOrganisms(extractedZooplankton);
      
      setBulkData({
        id: bulkId,
        date: data.date,
        area: data.area_name || data.area || 'Ukjent område',
        biologist: data.biologist || data.created_by || 'Biolog',
        algaeData: data.algae_data || {},
        zooplanktonData: data.zooplankton_data || {},
        colorOverrides: data.color_overrides || {},
        aiSummary: data.ai_summary
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bulk data:', error);
      setBulkData({
        id: bulkId,
        date: 'Ukjent',
        area: 'Kunne ikke laste data',
        biologist: 'Ukjent',
        algaeData: {},
        zooplanktonData: {},
        colorOverrides: {}
      });
      setLoading(false);
    }
  }, [bulkId]);

  useEffect(() => {
    fetchBulkData();
  }, [fetchBulkData]);

  const getColorForValue = (value, key, location, overrides) => {
    const overrideKey = `${key}_${location}`;
    const strValue = String(value).trim();
    
    const isBelow = strValue.startsWith('<');
    if (isBelow || strValue === '0' || strValue === '+') return 'bg-emerald-100 text-emerald-900';
    
    const cleanValue = strValue.replace(/[<>\s\u00A0]/g, '').replace(/,/g, '.');
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return value ? 'bg-emerald-100 text-emerald-900' : 'bg-white';
    if (numValue === 0) return 'bg-emerald-100 text-emerald-900';
    
    const isSum = key.toUpperCase().includes('SUM');
    const group = key.split('_')[0];
    const thresholds = isSum ? (groupThresholdMap[group]) : (thresholdMap[key]);
    
    let tY = 0, tO = 0, tR = 0;
    let hasThresholds = false;
    
    if (thresholds && thresholds.threshold_red != null) {
      tY = thresholds.threshold_yellow || 0;
      tO = thresholds.threshold_orange || 0;
      tR = thresholds.threshold_red || 0;
      hasThresholds = true;
    } else if (isSum && group === 'Kiselalger') {
      tY = 1000000; tO = 5000000; tR = 20000000;
      hasThresholds = true;
    }
    
    let thresholdColor = null;
    if (hasThresholds) {
      if (numValue >= tR) thresholdColor = 'red';
      else if (numValue >= tO) thresholdColor = 'orange';
      else if (numValue >= tY) thresholdColor = 'yellow';
      else thresholdColor = 'green';
    }
    
    const override = overrides?.[overrideKey];
    const effectiveColor = (override && thresholdColor && override !== thresholdColor) ? override : (thresholdColor || override || 'green');
    
    const colorMap = {
      'green': 'bg-emerald-100 text-emerald-900',
      'yellow': 'bg-amber-100 text-amber-900',
      'orange': 'bg-orange-200 text-orange-900',
      'red': 'bg-red-200 text-red-900'
    };
    return colorMap[effectiveColor] || 'bg-emerald-100 text-emerald-900';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laster prøvedata...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-full mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Button variant="ghost" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Tilbake
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Prøveresultater</h1>
              <div className="flex items-center space-x-4 text-slate-600">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(bulkData.date).toLocaleDateString('nb-NO')}</span>
                </div>
                <div>{bulkData.area}</div>
                <div>Analysert av: {bulkData.biologist}</div>
              </div>
            </div>
            
            <Button 
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="download-pdf-button"
            >
              {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Last ned PDF
            </Button>
          </div>
        </div>

        {/* Algae Results Table */}
        <Card className="shadow-xl border-0 mb-8">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center space-x-2">
              <Table className="w-6 h-6 text-teal-600" />
              <span>Algeresultater</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold">Orden/gruppe</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold">Evt. art</th>
                    <th className="border border-slate-300 px-3 py-3 text-center font-bold">Djup (m)</th>
                    {locations.map(loc => (
                      <th key={loc.full} className="border border-slate-300 px-3 py-3 text-center font-bold min-w-24">
                        <div className="text-sm">{loc.short}</div>
                        <div className="text-xs text-slate-600">(ant/l)</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {organisms.map(organism => {
                    const isSum = organism.isSum;
                    const key = organism.key;
                    
                    return (
                      <tr key={key} className={isSum ? 'bg-slate-100 font-semibold' : ''}>
                        <td className="border border-slate-300 px-4 py-3">{organism.group}</td>
                        <td className="border border-slate-300 px-4 py-3 italic">{organism.species}</td>
                        <td className="border border-slate-300 px-3 py-3 text-center">-</td>
                        {locations.map(location => {
                          const value = bulkData.algaeData[key]?.[location.full] || '';
                          return (
                            <td key={location.full} className="border border-slate-300 p-2">
                              <div className={`h-10 flex items-center justify-center text-sm font-semibold rounded-md ${
                                isSum ? (value ? getColorForValue(value, key, location.full, bulkData.colorOverrides) : 'bg-slate-200 text-slate-500') : getColorForValue(value, key, location.full, bulkData.colorOverrides)
                              }`}>
                                {value}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Zooplankton Results Table */}
        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center space-x-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <span>Zooplankton resultater</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold">Prøvetype</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold">Organisme</th>
                    {locations.map(loc => (
                      <th key={loc.full} className="border border-slate-300 px-3 py-3 text-center font-bold min-w-24">
                        <div className="text-sm">{loc.short}</div>
                        <div className="text-xs text-slate-600">(ant/m³)</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {zooplanktonOrganisms.map(organism => {
                    const key = organism.key;
                    
                    return (
                      <tr key={key} className="hover:bg-slate-50">
                        <td className="border border-slate-300 px-4 py-3">{organism.sampleType}</td>
                        <td className="border border-slate-300 px-4 py-3 italic">{organism.organism}</td>
                        {locations.map(location => {
                          const value = bulkData.zooplanktonData[key]?.[location.full] || '';
                          return (
                            <td key={location.full} className="border border-slate-300 p-2">
                              <div className="h-10 flex items-center justify-center text-sm font-semibold bg-blue-50 rounded-md">
                                {value}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}